/* The paged document editor — the one that looks like a word processor.
   Lays contenteditable text into US Letter pages and repaginates on the fly.

   Moved out of app.html unchanged apart from the onReady() wrapper. That
   wrapper is not cosmetic: this used to open with `if (!wrap) return;`, and
   running from the bundle in <head> — before the markup exists — that guard
   would fire on every load and the whole editor would silently not be there.
*/

import { onReady } from '../lib/ready.js';

export function initDocumentEditor() {
  const $ = id => document.getElementById(id);
  const wrap = $('doc-wrap');
  if (!wrap) return;   // now only true if the markup is genuinely absent

  const PAGE_H = 1056;          // US Letter at 96dpi
  const MARGIN = 96;            // 1in
  const INK = ['#111827', '#dc2626', '#ea580c', '#ca8a04', '#16a34a',
               '#0891b2', '#2563eb', '#4f46e5', '#9333ea', '#db2777', '#6b7280', '#ffffff'];
  const HL = ['#fef08a', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#e9d5ff', '#fed7aa', 'transparent'];

  let doc = null, saveTimer = null;
  const body = $('doc-body');

  const sizeSel = $('doc-size');
  sizeSel.innerHTML = [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48]
    .map(n => `<option value="${n}"${n === 11 ? ' selected' : ''}>${n}</option>`).join('');

  function markDirty() {
    if (!doc) return;
    $('doc-saved').textContent = 'Saving…';
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(save, 900);
  }

  /* Page count comes from how tall the text has grown. Text can straddle a
     boundary — laying real page boxes out around flowing content needs a
     pagination engine, which this is not. What it does give you is the page
     rhythm, the right margins and an honest page count. */
  function layoutPages() {
    const h = Math.max(body.scrollHeight, PAGE_H);
    const pages = Math.max(1, Math.ceil(h / PAGE_H));
    const host = $('doc-breaks');
    host.innerHTML = '';
    for (let i = 0; i < pages; i++) {
      const pg = document.createElement('div');
      pg.className = 'doc-page-bg';
      pg.style.top = (i * PAGE_H + i * 18) + 'px';
      pg.style.height = PAGE_H + 'px';
      host.appendChild(pg);
      const num = document.createElement('div');
      num.className = 'doc-page-num';
      num.style.top = (i * PAGE_H + i * 18 + PAGE_H - 26) + 'px';
      num.textContent = (i + 1) + ' / ' + pages;
      host.appendChild(num);
    }
    // The gaps between sheets have to exist in the text's own flow too, or the
    // words drift out of the last page as the document grows.
    body.style.minHeight = (pages * PAGE_H + (pages - 1) * 18) + 'px';
  }

  function exec(cmd, val) {
    body.focus();
    try { document.execCommand('styleWithCSS', false, true); } catch (e) {}
    document.execCommand(cmd, false, val === undefined ? null : val);
    syncButtons();
    markDirty();
    layoutPages();
  }

  /* execCommand('fontSize') only understands 1–7, which is a relic. The usual
     workaround: ask for size 7, then rewrite the <font> elements it just made
     into spans with the real point size. */
  function setFontSize(pt) {
    body.focus();
    document.execCommand('fontSize', false, '7');
    body.querySelectorAll('font[size="7"]').forEach(f => {
      const span = document.createElement('span');
      span.style.fontSize = pt + 'pt';
      while (f.firstChild) span.appendChild(f.firstChild);
      f.replaceWith(span);
    });
    markDirty();
    layoutPages();
  }

  function syncButtons() {
    ['bold', 'italic', 'underline'].forEach(c => {
      const b = wrap.querySelector(`.doc-f[data-cmd="${c}"]`);
      if (!b) return;
      let on = false;
      try { on = document.queryCommandState(c); } catch (e) {}
      b.classList.toggle('active', on);
    });
  }

  function swatches(host, list, onPick) {
    host.innerHTML = list.map(c =>
      `<button type="button" class="nb-swatch" data-c="${c}" style="background:${c === 'transparent' ? '#fff' : c}"${c === 'transparent' ? ' title="None"' : ''}></button>`).join('');
    host.querySelectorAll('[data-c]').forEach(b =>
      b.addEventListener('mousedown', e => { e.preventDefault(); onPick(b.dataset.c); }));
  }
  swatches($('doc-ink'), INK, c => {
    exec('foreColor', c);
    const sw = $('doc-ink-swatch');
    if (sw) sw.style.borderBottomColor = c;
    closePops();
  });
  swatches($('doc-hl'), HL, c => {
    // hiliteColor is the standard name; backColor is Firefox's.
    try { exec('hiliteColor', c); } catch (e) { exec('backColor', c); }
    closePops();
  });

  const POPS = ['doc-ink-pop', 'doc-hl-pop'];
  function closePops() { POPS.forEach(x => { const el = $(x); if (el) el.hidden = true; }); }
  function togglePop(id) {
    POPS.forEach(x => { const el = $(x); if (el) el.hidden = (x !== id) ? true : !el.hidden; });
  }
  $('doc-ink-btn').addEventListener('mousedown', e => { e.preventDefault(); e.stopPropagation(); togglePop('doc-ink-pop'); });
  $('doc-hl-btn').addEventListener('mousedown', e => { e.preventDefault(); e.stopPropagation(); togglePop('doc-hl-pop'); });
  document.addEventListener('click', e => {
    if (e.target.closest && (e.target.closest('.nb-pop') || e.target.closest('.nb-pop-wrap'))) return;
    closePops();
  });

  wrap.querySelectorAll('[data-cmd]').forEach(b =>
    b.addEventListener('mousedown', e => { e.preventDefault(); exec(b.dataset.cmd); }));
  $('doc-font').addEventListener('change', () => exec('fontName', $('doc-font').value));
  sizeSel.addEventListener('change', () => setFontSize(sizeSel.value));

  body.addEventListener('input', () => { markDirty(); layoutPages(); });
  body.addEventListener('keyup', syncButtons);
  body.addEventListener('mouseup', syncButtons);

  async function save() {
    if (!doc) return;
    try {
      /* innerText, not innerHTML, for `body`: previews, search and the AI all
         read that field and none of them want markup. */
      const patch = { html: body.innerHTML, body: body.innerText };
      if (typeof notesPatchActive === 'function') notesPatchActive(patch, true);
      $('doc-saved').textContent = 'Saved';
    } catch (e) {
      console.warn('document save failed:', e && e.message);
      $('doc-saved').textContent = 'Not saved';
    }
  }

  window.docOpen = function docOpen(note) {
    document.body.classList.add('doc-full');
    wrap.style.display = 'flex';
    if (doc && doc.id === note.id) { layoutPages(); return; }
    doc = note;
    $('doc-title').value = note.title || '';
    $('doc-saved').textContent = '';
    // Older documents only have plain text; keep their line breaks.
    body.innerHTML = note.html
      || (note.body ? note.body.split('\n').map(l =>
           '<div>' + (l ? l.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])) : '<br>') + '</div>').join('')
        : '');
    layoutPages();
    setTimeout(() => body.focus(), 60);
  };

  window.docClose = function docClose() {
    document.body.classList.remove('doc-full');
    wrap.style.display = 'none';
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; save(); }
    doc = null;
    closePops();
  };

  $('doc-back').addEventListener('click', () => {
    docClose();
    if (typeof notesCloseEditor === 'function') notesCloseEditor();
  });
  $('doc-title').addEventListener('input', () => {
    if (typeof notesPatchActive === 'function') notesPatchActive({ title: $('doc-title').value });
  });

  let rz = null;
  window.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(layoutPages, 120); });
  window.addEventListener('beforeunload', () => { if (doc) save(); });
}

onReady(initDocumentEditor);
